"use client";

import React from "react";
import type { BaseEntity, SimpleTableConfig, SimpleFieldConfig } from "../types";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useDetailActionContextOptional } from "@/contexts/DetailActionContext";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { useMobileDetailModalEditing } from "./useMobileDetailModalEditing";
import { MobileDetailModalField } from "./MobileDetailModalField";
import { MobileDetailModalMetadata } from "./MobileDetailModalMetadata";

interface SimpleTableMobileDetailModalProps<T extends BaseEntity> {
  item: T;
  config: SimpleTableConfig<T>;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onInlineEdit?: (item: T) => Promise<void>;
}

/**
 * Mobile-optimized detail modal for SimpleTable
 * Displays item details with inline editing capabilities
 * Refactored to use extracted components and utility functions
 * 
 * Memoized to prevent re-renders when parent re-renders with same item data
 */
function SimpleTableMobileDetailModalComponent<T extends { id: string }>({
  item,
  config,
  onEdit,
  onDelete,
  onInlineEdit,
}: SimpleTableMobileDetailModalProps<T>) {
  // Get current user for resolving "me" values
  const { appUser } = useUniversalAuth();

  // Role-based field filtering
  const { filterFieldsByRole, roleName } = useFieldFiltering();

  // Get action context from provider (will be provided by UniversalDetailModalContainer)
  const actionContext = useDetailActionContextOptional<T>();

  // Use mobile editing hook for state management
  const {
    editingField,
    isEditing,
    editedItem,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  } = useMobileDetailModalEditing(item);

  const handleSave = React.useCallback(
    async (fId: string) => {
      await handleSaveField(fId, onInlineEdit);
    },
    [handleSaveField, onInlineEdit]
  );

  // Get fields to display in detail view (exclude formOnly fields and filter by role)
  const fieldsToDisplay = React.useMemo(() => {
    // First filter fields by role
    const roleFilteredFields = filterFieldsByRole(config.fields);

    return Object.entries(roleFilteredFields).filter(([_, fieldConfig]) => !fieldConfig.formOnly);
  }, [config.fields, filterFieldsByRole]);

  return (
    <div className="space-y-6">
      {/* Field Details */}
      <div className="space-y-4">
        {fieldsToDisplay.map(([fieldId, fieldConfig]) => {
          const value = (editedItem[fieldId as keyof T] ?? null) as any;
          const isFieldEditing = editingField === fieldId;

          return (
            <MobileDetailModalField
              key={fieldId}
              fieldId={fieldId}
              fieldConfig={fieldConfig}
              value={value}
              item={item}
              editedValue={value}
              isEditing={isEditing}
              isFieldEditing={isFieldEditing}
              canEdit={!!config.actions?.edit}
              roleName={roleName ?? undefined}
              onEdit={handleFieldEdit}
              onSave={handleSave}
              onCancel={handleCancelEdit}
              onValueChange={handleFieldValueChange}
            />
          );
        })}
      </div>

      {/* Metadata */}
      <MobileDetailModalMetadata item={item} />
    </div>
  );
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

/**
 * Custom comparison for SimpleTableMobileDetailModal memoization.
 * Only re-render if item ID or config changes, not if item object reference changes.
 */
function areSimpleTableMobileDetailModalPropsEqual<T extends { id: string }>(
  prevProps: SimpleTableMobileDetailModalProps<T>,
  nextProps: SimpleTableMobileDetailModalProps<T>
): boolean {
  // Re-render if item ID changes
  if (prevProps.item?.id !== nextProps.item?.id) {
    return false;
  }

  // Re-render if config changes
  if (prevProps.config !== nextProps.config) {
    return false;
  }

  // Re-render if callbacks change
  if (
    prevProps.onEdit !== nextProps.onEdit ||
    prevProps.onDelete !== nextProps.onDelete ||
    prevProps.onInlineEdit !== nextProps.onInlineEdit
  ) {
    return false;
  }

  // Props are equal, don't re-render
  return true;
}

export const SimpleTableMobileDetailModal = React.memo(
  SimpleTableMobileDetailModalComponent,
  areSimpleTableMobileDetailModalPropsEqual
) as typeof SimpleTableMobileDetailModalComponent;
